import { useCallback, useMemo } from "react";

import { COMMANDBAR_FILTER_TABS } from "../constants/common";
import { useGlobalCommandbarStore } from "../store";
import { CommandbarShortcutKeys } from "../types/common";

export const useSearchInputKeyDown = () => {
  const activeFilterTab = useGlobalCommandbarStore(st => st.activeFilterTab);
  const activeCommandbarItemId = useGlobalCommandbarStore(
    st => st.activeCommandbarItemId,
  );
  const commandbarSearchItems = useGlobalCommandbarStore(
    st => st.commandbarSearchItems,
  );
  const commandbarPageItems = useGlobalCommandbarStore(
    st => st.commandbarPageItems,
  );
  const setActiveFilterTab = useGlobalCommandbarStore(
    st => st.setActiveFilterTab,
  );
  const setShotcutKeysPressed = useGlobalCommandbarStore(
    st => st.setShotcutKeysPressed,
  );
  const setIsOpenGlobalCommandbar = useGlobalCommandbarStore(
    st => st.setIsOpenGlobalCommandbar,
  );

  const getActiveTabIndex = useCallback(() => {
    return COMMANDBAR_FILTER_TABS.findIndex(
      tab => tab.label === activeFilterTab,
    );
  }, [activeFilterTab]);

  const activeNavigationItem = useMemo(() => {
    const allItems = [...commandbarSearchItems, ...commandbarPageItems];
    const item = allItems.find(
      item => item.id === activeCommandbarItemId && item.href,
    );

    return item;
  }, [activeCommandbarItemId, commandbarPageItems, commandbarSearchItems]);

  const onSearchInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isMetaKeyPressed = e.metaKey || e.ctrlKey;
    const isLeftSquareBracketKeyPressed = e.key === "[";
    const isRightSquareBracketKeyPressed = e.key === "]";
    const isEnterKeyPressed = e.key === "Enter";
    const isArrowUpKeyPressed = e.key === "ArrowUp";
    const isArrowDownKeyPressed = e.key === "ArrowDown";

    const isChangeTabShortcut =
      isMetaKeyPressed &&
      (isLeftSquareBracketKeyPressed || isRightSquareBracketKeyPressed);
    const isMetaEnterShortcut = isMetaKeyPressed && isEnterKeyPressed;
    const isAnyShortcutKeyPressed = isChangeTabShortcut || isMetaEnterShortcut;

    if (isAnyShortcutKeyPressed) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (isChangeTabShortcut) {
      const activeTabIndex = getActiveTabIndex();
      let nextTabIndex = 0;
      if (isLeftSquareBracketKeyPressed) {
        nextTabIndex =
          activeTabIndex === 0
            ? COMMANDBAR_FILTER_TABS.length - 1
            : activeTabIndex - 1;
      } else if (isRightSquareBracketKeyPressed) {
        nextTabIndex =
          activeTabIndex === COMMANDBAR_FILTER_TABS.length - 1
            ? 0
            : activeTabIndex + 1;
      }
      setActiveFilterTab(COMMANDBAR_FILTER_TABS[nextTabIndex].label);
    }

    if (isMetaKeyPressed) {
      setShotcutKeysPressed([CommandbarShortcutKeys.META]);
      if (isLeftSquareBracketKeyPressed) {
        setShotcutKeysPressed([
          CommandbarShortcutKeys.META,
          CommandbarShortcutKeys.LEFT_SQUARE_BRACKET,
        ]);
      } else if (isRightSquareBracketKeyPressed) {
        setShotcutKeysPressed([
          CommandbarShortcutKeys.META,
          CommandbarShortcutKeys.RIGHT_SQUARE_BRACKET,
        ]);
      } else if (isEnterKeyPressed) {
        setShotcutKeysPressed([
          CommandbarShortcutKeys.META,
          CommandbarShortcutKeys.ENTER,
        ]);
        if (activeNavigationItem) {
          setIsOpenGlobalCommandbar(false);
          window.open(activeNavigationItem.href, "_blank");
        }
      }
    } else if (isArrowUpKeyPressed) {
      setShotcutKeysPressed([CommandbarShortcutKeys.ARROW_UP]);
    } else if (isArrowDownKeyPressed) {
      setShotcutKeysPressed([CommandbarShortcutKeys.ARROW_DOWN]);
    }
  };

  const onSearchInputKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    setShotcutKeysPressed([]);
  };

  return { onSearchInputKeyDown, onSearchInputKeyUp };
};
